// Thin browser-direct client for the Anthropic Messages API.
//
// BYO model: the user pastes their own Anthropic key on /settings/anthropic-key,
// we stash it in localStorage and call api.anthropic.com directly from the
// browser. The user explicitly opted into this — we never proxy through our
// backend, and we never see the key.
//
// Endpoint: POST https://api.anthropic.com/v1/messages
// Required headers:
//   - Content-Type: application/json
//   - x-api-key: <user's key>
//   - anthropic-version: 2023-06-01
//   - anthropic-dangerous-direct-browser-access: true   (acks browser-direct)
//
// On success, json.content[0].text is the assistant's text reply. For the MJML
// generator we instruct Claude to return ONLY the MJML document — but we also
// defensively strip ```mjml / ``` fences in case the model wraps it anyway,
// and we validate the result actually starts with <mjml> before handing it to
// the editor.

export const ANTHROPIC_API_URL = 'https://api.anthropic.com/v1/messages';
export const ANTHROPIC_KEY_STORAGE_KEY = 'anthropic_api_key';
export const ANTHROPIC_LAST_MODEL_KEY = 'anthropic_last_model';
export const ANTHROPIC_LAST_TESTED_KEY = 'anthropic_key_last_tested_at';

export const DEFAULT_MODEL = 'claude-sonnet-4-6';

// Models surfaced in the picker. Order matters — first item is the default.
export const AVAILABLE_MODELS = [
	{ id: 'claude-sonnet-4-6', label: 'Sonnet 4.6 — balanced (default)' },
	{ id: 'claude-haiku-4-5',  label: 'Haiku 4.5 — fastest' },
];

// System prompt for MJML generation. Tight + explicit — Claude has a habit of
// wrapping code in markdown fences and prepending a sentence; we forbid both
// and STILL strip fences defensively in extractMjml().
export const MJML_SYSTEM_PROMPT = [
	'You are an expert email designer who writes MJML 4 markup for marketing emails.',
	'',
	'Rules:',
	'- Output ONLY a single valid MJML document. No preamble, no commentary, no markdown fences.',
	'- The response MUST begin with `<mjml>` and end with `</mjml>`.',
	'- Use semantic MJML tags: mj-section, mj-column, mj-text, mj-button, mj-image, mj-divider, mj-spacer, mj-social, mj-social-element.',
	'- Wrap everything in `<mj-body>`. Use `<mj-head>` only if you need fonts/styles/preview-text.',
	'- Use email-safe inline-friendly CSS via MJML attributes. Do not use `<style>` tags or external stylesheets.',
	'- Pick a clean color palette that fits the requested tone (default to a modern, minimal look with one accent color).',
	'- Use placeholder copy where the user did not specify exact text. Use `[COMPANY NAME]`, `[FIRST NAME]`, `[CTA URL]` style tokens for variables.',
	'- Always include at least one clear call-to-action button (`<mj-button>`).',
	'- Image URLs must be valid public placeholders (e.g. https://picsum.photos/600/300) — do NOT invent broken URLs.',
	'- Keep total width to 600px (the MJML default). Do not override `mj-body width`.',
	'- Aim for 4-8 sections — long enough to feel complete, short enough to render fast.',
].join('\n');

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class AnthropicError extends Error {
	constructor(message, { code, status, retryable } = {}) {
		super(message);
		this.name = 'AnthropicError';
		this.code = code || 'unknown';
		this.status = status || 0;
		this.retryable = !!retryable;
	}
}

// ---------------------------------------------------------------------------
// Key storage helpers
// ---------------------------------------------------------------------------

export function getStoredKey() {
	try {
		return (localStorage.getItem(ANTHROPIC_KEY_STORAGE_KEY) || '').trim();
	} catch (e) {
		return '';
	}
}

export function setStoredKey(key) {
	try {
		localStorage.setItem(ANTHROPIC_KEY_STORAGE_KEY, (key || '').trim());
	} catch (e) {
		// localStorage can throw in private-mode Safari; surface to caller.
		throw new AnthropicError('Could not save key to browser storage', { code: 'storage' });
	}
}

export function clearStoredKey() {
	try {
		localStorage.removeItem(ANTHROPIC_KEY_STORAGE_KEY);
		localStorage.removeItem(ANTHROPIC_LAST_TESTED_KEY);
	} catch (e) {
		// noop
	}
}

export function getLastTestedAt() {
	try {
		const raw = localStorage.getItem(ANTHROPIC_LAST_TESTED_KEY);
		const n = raw ? Number(raw) : 0;
		return Number.isFinite(n) && n > 0 ? n : 0;
	} catch (e) {
		return 0;
	}
}

export function recordTestedNow() {
	try {
		localStorage.setItem(ANTHROPIC_LAST_TESTED_KEY, String(Date.now()));
	} catch (e) {
		// noop
	}
}

export function getLastUsedModel() {
	try {
		const v = localStorage.getItem(ANTHROPIC_LAST_MODEL_KEY) || '';
		return v && AVAILABLE_MODELS.some((m) => m.id === v) ? v : DEFAULT_MODEL;
	} catch (e) {
		return DEFAULT_MODEL;
	}
}

export function setLastUsedModel(model) {
	try {
		if (model) localStorage.setItem(ANTHROPIC_LAST_MODEL_KEY, model);
	} catch (e) {
		// noop
	}
}

// Anthropic keys all start with `sk-ant-`. Keep the check loose — exact
// length/format has changed across key versions over time.
export function looksLikeAnthropicKey(key) {
	if (!key || typeof key !== 'string') return false;
	const k = key.trim();
	return k.startsWith('sk-ant-') && k.length >= 20;
}

// Mask a key for display: first 8 chars then ellipsis.
export function maskKey(key) {
	if (!key) return '';
	const k = String(key).trim();
	if (k.length <= 8) return `${k}…`;
	return `${k.slice(0, 8)}…`;
}

// ---------------------------------------------------------------------------
// MJML extraction / validation
// ---------------------------------------------------------------------------

// Pull the MJML document out of Claude's reply. Strips:
//   - leading prose ("Here's the MJML you asked for:")
//   - markdown fences (```mjml ... ``` or ``` ... ```)
//   - trailing prose
// Throws if no <mjml>...</mjml> can be found.
export function extractMjml(rawText) {
	if (!rawText || typeof rawText !== 'string') {
		throw new AnthropicError('Claude returned an empty response', { code: 'empty_response' });
	}

	let text = rawText.trim();

	// Strip a leading ```mjml or ```html or ``` fence opener.
	text = text.replace(/^```(?:mjml|html|xml)?\s*\n?/i, '');
	// Strip the trailing ``` if present.
	text = text.replace(/\n?```\s*$/i, '');
	text = text.trim();

	// Find the <mjml>…</mjml> block — Claude sometimes adds a sentence above
	// or below despite the system prompt. We trust the markup, not the prose.
	const match = text.match(/<mjml[\s\S]*?<\/mjml>/i);
	if (!match) {
		throw new AnthropicError(
			'Claude did not return a valid MJML document. Try rewording your prompt.',
			{ code: 'invalid_mjml' },
		);
	}

	const mjml = match[0].trim();
	if (!/<mj-body[\s>]/i.test(mjml)) {
		throw new AnthropicError(
			'Claude returned MJML without a <mj-body>. Try again.',
			{ code: 'invalid_mjml' },
		);
	}
	return mjml;
}

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

function buildHeaders(key) {
	return {
		'Content-Type': 'application/json',
		'x-api-key': key,
		'anthropic-version': '2023-06-01',
		// Required to opt into browser-direct calls. Without this header the
		// API rejects the request with CORS-level error messaging.
		'anthropic-dangerous-direct-browser-access': 'true',
	};
}

function pickErrorFromStatus(status, bodyText) {
	// Try to lift Anthropic's structured error message.
	let detail = '';
	try {
		const json = JSON.parse(bodyText || '{}');
		if (json && json.error && json.error.message) detail = json.error.message;
	} catch (e) {
		detail = (bodyText || '').slice(0, 200);
	}

	if (status === 401 || status === 403) {
		return new AnthropicError(
			'Your Anthropic API key was rejected. Double-check the key on /settings/anthropic-key.',
			{ code: 'unauthorized', status, retryable: false },
		);
	}
	if (status === 429) {
		return new AnthropicError(
			'Anthropic rate-limited this key. Wait a moment and try again.',
			{ code: 'rate_limited', status, retryable: true },
		);
	}
	if (status >= 500) {
		return new AnthropicError(
			'Anthropic API is having trouble right now. Try again in a few seconds.',
			{ code: 'server_error', status, retryable: true },
		);
	}
	return new AnthropicError(
		detail || `Anthropic API returned HTTP ${status}.`,
		{ code: 'http_error', status, retryable: false },
	);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Send a Messages API call directly to Anthropic from the browser.
 *
 * @param {object} args
 * @param {string} args.system        - system prompt
 * @param {string} args.userPrompt    - user message content
 * @param {string} [args.model]       - model id (defaults to DEFAULT_MODEL)
 * @param {number} [args.maxTokens]   - cap (default 4096)
 * @param {string} [args.key]         - explicit key override (otherwise pulled from localStorage)
 * @returns {Promise<{ text: string, raw: object }>}
 */
export async function callAnthropic({
	system,
	userPrompt,
	model = DEFAULT_MODEL,
	maxTokens = 4096,
	key,
} = {}) {
	const apiKey = (key || getStoredKey()).trim();
	if (!apiKey) {
		throw new AnthropicError(
			'No Anthropic API key set. Add one on /settings/anthropic-key.',
			{ code: 'missing_key', retryable: false },
		);
	}
	if (!looksLikeAnthropicKey(apiKey)) {
		throw new AnthropicError(
			'That doesn\'t look like an Anthropic key (should start with sk-ant-).',
			{ code: 'invalid_key_format', retryable: false },
		);
	}
	if (!userPrompt || !String(userPrompt).trim()) {
		throw new AnthropicError('Empty prompt — describe what you want first.', { code: 'empty_prompt' });
	}

	let res;
	try {
		res = await fetch(ANTHROPIC_API_URL, {
			method: 'POST',
			headers: buildHeaders(apiKey),
			body: JSON.stringify({
				model,
				max_tokens: maxTokens,
				system: system || '',
				messages: [{ role: 'user', content: String(userPrompt) }],
			}),
		});
	} catch (err) {
		throw new AnthropicError(
			`Could not reach Anthropic API: ${err && err.message ? err.message : 'network error'}`,
			{ code: 'network', retryable: true },
		);
	}

	const bodyText = await res.text();
	if (!res.ok) {
		throw pickErrorFromStatus(res.status, bodyText);
	}

	let json;
	try {
		json = JSON.parse(bodyText);
	} catch (e) {
		throw new AnthropicError('Anthropic returned a malformed response.', { code: 'malformed_response' });
	}

	const blocks = Array.isArray(json && json.content) ? json.content : [];
	const text = blocks
		.filter((b) => b && b.type === 'text' && typeof b.text === 'string')
		.map((b) => b.text)
		.join('\n')
		.trim();

	if (!text) {
		throw new AnthropicError('Claude returned no text content.', { code: 'empty_response' });
	}
	return { text, raw: json };
}

/**
 * Generate an MJML document for the given user prompt.
 *
 * @param {object} args
 * @param {string} args.prompt
 * @param {string} [args.model]
 * @returns {Promise<{ mjml: string, model: string }>}
 */
export async function generateMjmlTemplate({ prompt, model = DEFAULT_MODEL } = {}) {
	const { text } = await callAnthropic({
		system: MJML_SYSTEM_PROMPT,
		userPrompt: prompt,
		model,
		maxTokens: 4096,
	});
	const mjml = extractMjml(text);
	return { mjml, model };
}

/**
 * Tiny round-trip to verify the stored key works. Used by the "Test connection"
 * button on /settings/anthropic-key. Keeps token use minimal (1-token cap, "ok"
 * prompt).
 */
export async function testConnection({ key, model = DEFAULT_MODEL } = {}) {
	await callAnthropic({
		key,
		model,
		system: 'Reply with the single word: ok',
		userPrompt: 'ping',
		maxTokens: 8,
	});
	recordTestedNow();
	return true;
}
